import { ArrowDown, ArrowUp, Minus, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface KpiCardProps {
  label: string;
  value: string;
  trend?: { value: number; direction: 'up' | 'down' | 'flat' };
  hint?: string;
  icon?: LucideIcon;
  href?: string;
  variant?: 'default' | 'destructive' | 'warning';
}

export function KpiCard({
  label,
  value,
  trend,
  hint,
  icon: Icon,
  href,
  variant = 'default',
}: KpiCardProps) {
  const TrendIcon =
    trend?.direction === 'up' ? ArrowUp : trend?.direction === 'down' ? ArrowDown : Minus;
  const card = (
    <Card
      className={cn(
        'h-full overflow-hidden transition-colors',
        href && 'hover:bg-card/80',
        variant === 'destructive' && 'border-l-2 border-l-destructive',
        variant === 'warning' && 'border-l-2 border-l-warning',
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          {Icon && <Icon className="size-3.5" />}
          <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
        </div>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground">
            {value}
          </span>
          {trend && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums',
                trend.direction === 'up' && 'border-success/30 bg-success/10 text-success',
                trend.direction === 'down' &&
                  'border-destructive/30 bg-destructive/10 text-destructive',
                trend.direction === 'flat' && 'border-border bg-muted text-muted-foreground',
              )}
            >
              <TrendIcon className="size-3" />
              {Math.abs(trend.value)}%
            </span>
          )}
        </div>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );

  if (!href) return card;
  return (
    <Link
      to={href}
      className="block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {card}
    </Link>
  );
}
